import { http } from '../../services/http';
import { newIdempotencyKey } from '../../utils/idempotency';

export type StageCategory = 'open' | 'on_hold' | 'won' | 'lost';
export type Priority = 'low' | 'normal' | 'high' | 'urgent';
type Ref = { id: string; name: string | null } | null;

export interface Stage {
  id: string;
  code: string;
  name: string;
  category: StageCategory;
  position: number;
  color: string | null;
  isInitial: boolean;
  systemRole: 'assigned' | 'contacted' | null;
  slaMinutes: number | null;
  requiredFields: string[];
  /** Phase 10: what happens when a lead enters this stage. */
  entryActions?: EntryAction[];
  isActive: boolean;
}

export type EntryAction =
  | {
      type: 'create_follow_up';
      taskType: 'call' | 'whatsapp' | 'email' | 'meeting' | 'site_visit' | 'other';
      dueInMinutes: number;
      title?: string;
    }
  | { type: 'set_priority'; priority: 'low' | 'normal' | 'high' | 'urgent' };

export interface Pipeline {
  id: string;
  name: string;
  isDefault: boolean;
  status: 'active' | 'inactive';
  autoAdvanceOnAssign: boolean;
  autoAdvanceOnContact: boolean;
  branch: Ref;
  stages: Stage[];
  transitions: { fromStageId: string; toStageId: string }[];
}

export interface Source {
  id: string;
  code: string;
  name: string;
  channelType: string;
  isActive: boolean;
}
export interface Tag {
  id: string;
  name: string;
  color: string | null;
}
export interface LostReason {
  id: string;
  name: string;
  isActive: boolean;
  position: number;
}
export type FieldType =
  | 'text'
  | 'long_text'
  | 'number'
  | 'decimal'
  | 'date'
  | 'datetime'
  | 'boolean'
  | 'select'
  | 'multi_select'
  | 'phone'
  | 'email'
  | 'url';
export interface FieldDefinition {
  id: string;
  key: string;
  label: string;
  dataType: FieldType;
  options: { value: string; label: string }[];
  validation: { min?: number; max?: number; maxLength?: number; pattern?: string };
  isRequired: boolean;
  isFilterable: boolean;
  showInList: boolean;
  pipelineId: string | null;
  position: number;
  isActive: boolean;
}

export interface LeadOptions {
  pipelines: Pipeline[];
  sources: Source[];
  tags: Tag[];
  lostReasons: LostReason[];
  fields: FieldDefinition[];
  settings: {
    currency: string;
    timezone: string;
    leadNumberPrefix: string;
    requireLostReason: boolean;
    duplicateOverrideRequires: 'reason' | 'merge_permission';
    transferRequiresApproval: boolean;
    followupRequiresOutcome: boolean;
    defaultReminderOffsetMin: number;
  };
}

export interface LeadSummary {
  id: string;
  leadNumber: string;
  fullName: string;
  phone: string | null;
  email: string | null;
  companyName: string | null;
  interest: string | null;
  priority: Priority;
  status: StageCategory;
  qualificationStatus: string;
  stage: { id: string; name: string; category: StageCategory; color: string | null };
  pipeline: { id: string; name: string };
  branch: { id: string; name: string };
  source: Ref;
  owner: Ref;
  team: Ref;
  estimatedValue: string | null;
  currency: string;
  captureChannel: string;
  assignedAt: string | null;
  nextFollowUpAt: string | null;
  lastActivityAt: string | null;
  stageEnteredAt: string;
  archivedAt: string | null;
  isMerged: boolean;
  isPossibleDuplicate: boolean;
  version: number;
  createdAt: string;
  updatedAt: string;
  tags: Tag[];
  customFields: Record<string, unknown>;
}

export interface Assignment {
  id: string;
  type: 'initial' | 'reassign' | 'transfer' | 'auto' | 'unassign';
  assignee: Ref;
  team: Ref;
  branch: Ref;
  previousAssignee: Ref;
  initiatedBy: Ref;
  reason: string | null;
  approvalState: string;
  isCurrent: boolean;
  ackStatus: string;
  ackDeadlineAt: string | null;
  acknowledgedAt: string | null;
  firstActionAt: string | null;
  endedAt: string | null;
  createdAt: string;
}

export type LeadAction =
  | 'edit'
  | 'change_stage'
  | 'archive'
  | 'restore'
  | 'reopen'
  | 'merge'
  | 'transfer'
  | 'assign'
  | 'add_activity'
  | 'add_follow_up';

export interface LeadDetail extends LeadSummary {
  altPhone: string | null;
  notes: string | null;
  qualificationNotes: string | null;
  campaign: string | null;
  utm: Record<string, string | null>;
  firstContactedAt: string | null;
  lastContactedAt: string | null;
  wonAt: string | null;
  lostAt: string | null;
  closedAt: string | null;
  lostReason: Ref;
  lostReasonNote: string | null;
  conversionValue: string | null;
  createdBy: Ref;
  currentAssignment: Assignment | null;
  pendingFollowUps: {
    count: number;
    items: { id: string; title: string; dueAt: string; taskType: string }[];
  };
  pendingTransferRequest: { id: string; createdAt: string; expiresAt: string } | null;
  possibleDuplicateOf: { id: string; leadNumber: string; fullName: string } | null;
  mergedInto: { id: string; leadNumber: string } | null;
  allowedActions: LeadAction[];
}

export interface Activity {
  id: string;
  lead: { id: string; leadNumber: string } | null;
  type: string;
  direction: 'inbound' | 'outbound' | null;
  outcomeCode: string | null;
  customerResponse: string | null;
  notes: string | null;
  nextAction: string | null;
  durationSeconds: number | null;
  occurredAt: string;
  performedBy: Ref;
  isSystem: boolean;
  followUpTaskId: string | null;
  correctsActivityId: string | null;
  correctedBy: string[];
  metadata: Record<string, unknown> | null;
}

export interface FollowUp {
  id: string;
  lead: { id: string; leadNumber: string; fullName: string; phone: string | null; priority: Priority } | null;
  taskType: string;
  title: string;
  notes: string | null;
  priority: Priority;
  dueAt: string;
  status: 'pending' | 'completed' | 'cancelled';
  isOverdue: boolean;
  assignee: Ref;
  completedAt: string | null;
  cancelledReason: string | null;
  version: number;
}

export interface TransferRequest {
  id: string;
  lead: { id: string; leadNumber: string; fullName: string; priority: Priority; status: string } | null;
  from: Ref;
  to: Ref;
  toTeam: Ref;
  toBranch: Ref;
  requestedBy: Ref;
  reason: string;
  status: string;
  decidedBy: Ref;
  decisionNote: string | null;
  expiresAt: string;
  createdAt: string;
}

export interface WorkloadRow {
  user: { id: string; name: string };
  branch: Ref;
  isAssignable: boolean;
  maxOpenLeads: number | null;
  openLeads: number;
  assignedToday: number;
  overdueFollowUps: number;
  awaitingAck: number;
}

export interface BoardColumn {
  stage: { id: string; name: string; category: StageCategory; color: string | null; position: number };
  count: number;
  totalValue: string;
  leads: LeadSummary[];
}

export interface DuplicateMatch {
  id: string;
  leadNumber: string;
  fullName: string;
  status: string;
  stage: string;
  assignedTo: { id: string; name: string | null } | null;
  branch: string;
}

export interface CaptureForm {
  id: string;
  name: string;
  status: 'active' | 'revoked';
  tokenPrefix: string;
  branch: Ref;
  source: Ref;
  pipeline: Ref;
  defaultPriority: Priority;
  fields: { key: string; label?: string; required: boolean }[];
  allowedOrigins: string[];
  rateLimitPerHour: number;
  successMessage: string | null;
  lastSubmissionAt: string | null;
  token?: string;
}

export interface LeadImport {
  id: string;
  fileName: string | null;
  status: string;
  columnMapping: Record<string, string>;
  totalRows: number;
  createdCount: number;
  duplicateCount: number;
  errorCount: number;
  preview?: { row: number; input: Record<string, unknown>; status: string; errors?: string[] }[];
  errors?: { row: number; errors?: string[] }[];
  createdAt: string;
}

export type LeadFilters = Record<string, string>;
export interface LeadListParams {
  page?: number;
  pageSize?: number;
  sort?: string;
  q?: string;
  filters?: LeadFilters;
}

const key = () => newIdempotencyKey();

function listQuery(params: LeadListParams) {
  const query: Record<string, string | number | undefined> = {
    page: params.page,
    pageSize: params.pageSize,
    sort: params.sort,
    q: params.q,
  };
  for (const [k, v] of Object.entries(params.filters ?? {})) if (v) query[`filter[${k}]`] = v;
  return query;
}

/** GET /leads/stats: dashboard figures within the caller's lead scope (docs/api/10). */
export interface LeadStats {
  total: number;
  byStatus: Record<'open' | 'on_hold' | 'won' | 'lost', number>;
  byPriority: Record<'low' | 'normal' | 'high' | 'urgent', number>;
  notContacted: number;
  unassigned: number;
  bySource: { id: string | null; name: string; count: number }[];
  createdByDay: { date: string; count: number }[];
  timezone: string;
}

export const leadsApi = {
  options: () => http.get<LeadOptions>('/lead-options'),
  stats: (days: number) => http.get<LeadStats>('/leads/stats', { query: { days } }),
  workQueues: () =>
    http.get<{ noContact: number; stale: number; needsReassignment: number; unassigned: number | null }>(
      '/work-queues',
    ),
  list: (params: LeadListParams) => http.list<LeadSummary[]>('/leads', { query: listQuery(params) }),
  board: (params: LeadListParams & { pipelineId?: string }) =>
    http.get<{ pipeline: { id: string; name: string }; columns: BoardColumn[] }>('/leads', {
      query: {
        ...listQuery({
          ...params,
          filters: { ...params.filters, ...(params.pipelineId ? { pipelineId: params.pipelineId } : {}) },
        }),
        view: 'board',
      },
    }),
  unassigned: (params: LeadListParams) =>
    http.list<LeadSummary[]>('/leads/unassigned', { query: listQuery(params) }),
  get: (id: string) => http.get<LeadDetail>(`/leads/${id}`),
  create: (body: Record<string, unknown>) => http.post<LeadDetail>('/leads', body, { idempotencyKey: key() }),
  update: (id: string, body: Record<string, unknown>) => http.patch<LeadDetail>(`/leads/${id}`, body),
  duplicateCheck: (body: { phone?: string; email?: string; excludeLeadId?: string }) =>
    http.post<{ matchedOn: string[]; matches: DuplicateMatch[]; hiddenMatches: number }>(
      '/leads/duplicate-check',
      body,
    ),
  changeStage: (id: string, body: Record<string, unknown>) =>
    http.post<LeadDetail>(`/leads/${id}/stage`, body, { idempotencyKey: key() }),
  reopen: (id: string, body: Record<string, unknown>) => http.post<LeadDetail>(`/leads/${id}/reopen`, body),
  archive: (id: string) => http.post<LeadDetail>(`/leads/${id}/archive`),
  restore: (id: string) => http.post<LeadDetail>(`/leads/${id}/restore`),
  merge: (id: string, body: Record<string, unknown>) =>
    http.post<LeadDetail>(`/leads/${id}/merge`, body, { idempotencyKey: key() }),
  setTags: (id: string, tagIds: string[]) => http.put<Tag[]>(`/leads/${id}/tags`, { tagIds }),
  history: (id: string) =>
    http.get<{
      stages: {
        from: { id: string; name: string } | null;
        to: { id: string; name: string } | null;
        changedBy: Ref;
        changedAt: string;
        durationSeconds: number | null;
        note: string | null;
      }[];
      assignments: Assignment[];
    }>(`/leads/${id}/history`),
  bulk: (body: { action: string; leadIds: string[]; params?: Record<string, unknown> }) =>
    http.post<{
      results: { id: string; ok: boolean; error?: { code: string; message: string } }[];
      succeeded: number;
      failed: number;
    }>('/leads/bulk', body, { idempotencyKey: key() }),

  assign: (id: string, body: Record<string, unknown>) =>
    http.post<{ assignment: Assignment; lead: { id: string; version: number } }>(
      `/leads/${id}/assignments`,
      body,
      {
        idempotencyKey: key(),
      },
    ),
  transfer: (id: string, body: Record<string, unknown>) =>
    http.post<{ status: 'completed' | 'pending' }>(`/leads/${id}/transfer-requests`, body, {
      idempotencyKey: key(),
    }),
  assignees: (query: { leadId?: string; q?: string; purpose?: 'assign' | 'transfer' }) =>
    http.get<WorkloadRow[]>('/assignments/assignees', { query }),
  workload: () => http.get<WorkloadRow[]>('/staff/workload'),
  transferRequests: (status?: string) =>
    http.list<TransferRequest[]>('/transfer-requests', {
      query: { 'filter[status]': status, pageSize: 100 },
    }),
  decideTransfer: (id: string, decision: 'approve' | 'reject', note?: string) =>
    http.post<unknown>(
      `/transfer-requests/${id}/${decision}`,
      { note: note ?? null },
      { idempotencyKey: key() },
    ),
  cancelTransfer: (id: string) =>
    http.post<TransferRequest>(`/transfer-requests/${id}/cancel`, undefined, { idempotencyKey: key() }),

  activities: (id: string, cursor?: string) =>
    http.list<Activity[]>(`/leads/${id}/activities`, { query: { cursor, limit: 50 } }),
  logActivity: (id: string, body: Record<string, unknown>) =>
    http.post<Activity>(`/leads/${id}/activities`, body, { idempotencyKey: key() }),
  correctActivity: (id: string, activityId: string, body: Record<string, unknown>) =>
    http.post<Activity>(`/leads/${id}/activities/${activityId}/corrections`, body),

  followUps: (filters: Record<string, string | undefined>, page = 1) =>
    http.list<FollowUp[]>('/follow-ups', {
      query: {
        page,
        pageSize: 50,
        ...Object.fromEntries(Object.entries(filters).map(([k, v]) => [`filter[${k}]`, v])),
      },
    }),
  createFollowUp: (id: string, body: Record<string, unknown>) =>
    http.post<FollowUp>(`/leads/${id}/follow-ups`, body, { idempotencyKey: key() }),
  updateFollowUp: (id: string, body: Record<string, unknown>) =>
    http.patch<FollowUp>(`/follow-ups/${id}`, body),
  completeFollowUp: (id: string, body: Record<string, unknown>) =>
    http.post<{ task: FollowUp; nextFollowUp: FollowUp | null }>(`/follow-ups/${id}/complete`, body, {
      idempotencyKey: key(),
    }),
  cancelFollowUp: (id: string, reason: string) => http.post<FollowUp>(`/follow-ups/${id}/cancel`, { reason }),

  imports: () => http.get<LeadImport[]>('/leads/imports'),
  createImport: (body: Record<string, unknown>) => http.post<LeadImport>('/leads/imports', body),
  importStatus: (id: string) => http.get<LeadImport>(`/leads/imports/${id}`),
  startImport: (id: string) => http.post<LeadImport>(`/leads/imports/${id}/start`),
};

/** Lead configuration (settings screens). */
export const configApi = {
  pipelines: () => http.get<Pipeline[]>('/pipelines'),
  createPipeline: (body: Record<string, unknown>) => http.post<Pipeline>('/pipelines', body),
  updatePipeline: (id: string, body: Record<string, unknown>) =>
    http.patch<Pipeline>(`/pipelines/${id}`, body),
  deletePipeline: (id: string) => http.delete<void>(`/pipelines/${id}`),
  createStage: (pipelineId: string, body: Record<string, unknown>) =>
    http.post<Stage>(`/pipelines/${pipelineId}/stages`, body),
  updateStage: (id: string, body: Record<string, unknown>) => http.patch<Stage>(`/stages/${id}`, body),
  deleteStage: (id: string) => http.delete<void>(`/stages/${id}`),
  reorderStages: (pipelineId: string, stageIds: string[]) =>
    http.put<Pipeline>(`/pipelines/${pipelineId}/stages/order`, { stageIds }),
  setTransitions: (pipelineId: string, transitions: { fromStageId: string; toStageId: string }[]) =>
    http.put<unknown>(`/pipelines/${pipelineId}/transitions`, { transitions }),
  list: <T>(path: 'lead-sources' | 'lead-tags' | 'lost-reasons' | 'lead-fields') => http.get<T[]>(`/${path}`),
  create: <T>(path: string, body: Record<string, unknown>) => http.post<T>(`/${path}`, body),
  update: <T>(path: string, id: string, body: Record<string, unknown>) =>
    http.patch<T>(`/${path}/${id}`, body),
  remove: (path: string, id: string) => http.delete<void>(`/${path}/${id}`),
  templates: () =>
    http.get<
      {
        code: string;
        name: string;
        description: string;
        pipelineName: string;
        stages: { name: string }[];
        fields: { label: string }[];
      }[]
    >('/lead-config/templates'),
  applyTemplate: (code: string) => http.post<Pipeline>(`/lead-config/templates/${code}/apply`, {}),

  forms: () => http.get<CaptureForm[]>('/lead-capture-forms'),
  createForm: (body: Record<string, unknown>) => http.post<CaptureForm>('/lead-capture-forms', body),
  updateForm: (id: string, body: Record<string, unknown>) =>
    http.patch<CaptureForm>(`/lead-capture-forms/${id}`, body),
  rotateForm: (id: string) => http.post<CaptureForm>(`/lead-capture-forms/${id}/rotate-token`),
  revokeForm: (id: string) => http.post<CaptureForm>(`/lead-capture-forms/${id}/revoke`),
};

export const leadKeys = {
  options: ['lead-options'] as const,
  all: ['leads'] as const,
  stats: (days: number) => ['leads', 'stats', days] as const,
  workQueues: ['leads', 'work-queues'] as const,
  list: (params: LeadListParams) => ['leads', 'list', params] as const,
  board: (params: unknown) => ['leads', 'board', params] as const,
  unassigned: (params: LeadListParams) => ['leads', 'unassigned', params] as const,
  detail: (id: string) => ['leads', 'detail', id] as const,
  activities: (id: string) => ['leads', 'activities', id] as const,
  history: (id: string) => ['leads', 'history', id] as const,
  followUps: (filters: unknown) => ['follow-ups', filters] as const,
  transfers: (status?: string) => ['transfer-requests', status ?? 'all'] as const,
  workload: ['workload'] as const,
  assignees: (query: unknown) => ['assignees', query] as const,
  config: (what: string) => ['lead-config', what] as const,
  imports: ['lead-imports'] as const,
};

export const PRIORITY_LABEL: Record<Priority, string> = {
  low: 'Low',
  normal: 'Normal',
  high: 'High',
  urgent: 'Urgent',
};
export const PRIORITY_TONE: Record<Priority, 'neutral' | 'info' | 'warning' | 'danger'> = {
  low: 'neutral',
  normal: 'info',
  high: 'warning',
  urgent: 'danger',
};
export const CATEGORY_TONE: Record<StageCategory, 'info' | 'warning' | 'success' | 'danger'> = {
  open: 'info',
  on_hold: 'warning',
  won: 'success',
  lost: 'danger',
};
export const ACTIVITY_LABEL: Record<string, string> = {
  call: 'Call',
  whatsapp: 'WhatsApp',
  email: 'Email',
  meeting: 'Meeting',
  site_visit: 'Site visit',
  note: 'Note',
  follow_up: 'Follow-up',
  status_change: 'Stage change',
  assignment: 'Assignment',
  transfer: 'Transfer',
  conversion: 'Won',
  lost: 'Lost',
  merge: 'Merge',
  system: 'System',
  notification: 'Notification',
};

export function formatDateTime(value: string | null | undefined, timeZone?: string): string {
  if (!value) return '—';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
    ...(timeZone ? { timeZone } : {}),
  }).format(new Date(value));
}

export function formatMoney(value: string | null | undefined, currency = 'INR'): string {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 0 }).format(
    Number(value),
  );
}
